import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft, FiMoon, FiSun } from "react-icons/fi";
import {
  createAnimation,
  updateAnimation,
} from "../../../services/animation.service.js";

const AnimationEditorHeader = ({
  animationMetadata,
  onSaved,
  pushLog,
  theme = "dark",
  onToggleTheme,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState(8);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setTitle(animationMetadata?.title || "");
    setDescription(animationMetadata?.description || "");
    setDuration(animationMetadata?.duration ?? 8);
  }, [animationMetadata]);

  const handleSave = async () => {
    if (!title.trim()) {
      pushLog("error", "Title is required");
      return;
    }
    if (Number.isNaN(Number(duration)) || Number(duration) < 0) {
      pushLog("error", "Duration must be a valid positive number");
      return;
    }

    const payload = {
      title: title.trim(),
      description: description.trim(),
      duration: Number(duration),
    };

    setSaving(true);
    try {
      let id = animationMetadata?.animationId;
      if (!id) {
        const created = await createAnimation(payload);
        id = created.animationId;
        navigate(`/studio/animations/${id}`, { replace: true });
      } else {
        await updateAnimation(id, payload);
      }
      pushLog("info", "Animation metadata saved");
      await onSaved?.();
    } catch (error) {
      pushLog("error", error.message || "Failed to save animation");
    } finally {
      setSaving(false);
    }
  };

  return (
    <header className="animation-workbench-header">
      <button
        type="button"
        className="animation-workbench-btn animation-workbench-back-btn animation-workbench-back-action-btn"
        onClick={() => navigate("/studio/animations")}
        title="Back to animations"
      >
        <FiArrowLeft />
      </button>

      <div className="animation-workbench-header-fields">
        <div className="animation-workbench-inline-field">
          <div className="animation-workbench-inline-label">Title</div>
          <input
            value={title}
            maxLength={50}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Animation title"
            disabled={saving}
          />
        </div>
        <div className="animation-workbench-inline-field">
          <div className="animation-workbench-inline-label">Description</div>
          <input
            value={description}
            maxLength={150}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short description"
            disabled={saving}
          />
        </div>
        <div className="animation-workbench-inline-field">
          <div className="animation-workbench-inline-label">Duration</div>
          <input
            type="number"
            min={0}
            step="0.01"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            disabled={saving}
          />
        </div>
      </div>

      <div className="animation-workbench-header-actions">
        <button
          type="button"
          className="animation-workbench-btn animation-workbench-theme-btn"
          onClick={onToggleTheme}
          title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
        >
          {theme === "dark" ? <FiSun /> : <FiMoon />}
        </button>
        <button
          type="button"
          className="animation-workbench-btn animation-workbench-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {animationMetadata?.animationId ? "Save" : "Create"}
        </button>
      </div>
    </header>
  );
};

export default AnimationEditorHeader;
